<template>
  <div>
    <!-- display the date if necessary -->
    <div v-if="showDate()" class="text-caption separator mt-2">
      <div class="date mb-1">
        {{ simplifyDate(message.date, "LL") }}
      </div>
    </div>
    <!-- display the message -->
    <div :class="'chat-bubble-container ' + (showDate() ? 'mb-2' : '')" @click="toggleTime">
      <template v-if="message.who_sent !== 0">
        <v-menu offset-y top open-on-hover :close-on-content-click="false" close-delay="160" max-width="100px">
          <template v-slot:activator="{ props }">
            <div v-if="message.who_sent !== userId" style="margin-left: 0.3rem;">
              <UserPopup :user_id="message.who_sent" />
            </div>
            <div v-bind="props"
              :class="message.who_sent === userId ? 'chat-bubble msg-self' : 'chat-bubble msg-friend'">
              <div @click="handleReferenceClick" style="color: grey;"
                v-if="refrenceConvo && refrenceConvo.media_id == null && refrenceConvo.message && message.ref!=null">
                <v-icon v-if="message.ref!=null">mdi-hand-pointing-up</v-icon> {{ refrenceConvo.message }}
              </div>
              <div @click="handleReferenceClick" v-else-if="refrenceConvo" style="margin-bottom: 3px;background-color: #ebeced;">
                <v-icon v-if="message.ref!=null">mdi-hand-pointing-up</v-icon>
                <v-icon v-if="message.ref!=null">mdi-image</v-icon>
                <v-img v-on:click="openImg(fetchImg(refrenceConvo.convo_message_id, refrenceConvo.media_id))"
                  max-height="120" max-width="200" :src="imgSrc"></v-img>
              </div>
              <div :id="'message-' + message.convo_message_id">
                <!-- Video call link always takes priority, regardless of mentions -->
                <template v-if="videoCallInfo(message.message)">
                  <div v-if="videoCallInfo(message.message).prefix" style="margin-bottom:6px; white-space:pre-wrap; word-break:break-word;">
                    <template v-if="message.convo_mentions && message.convo_mentions.length > 0">
                      <span v-for="item in splitPrefixByMentions(message, videoCallInfo(message.message).prefix)" :key="item.index" style="display:inline">
                        <UserText v-if="item.user_id" :text="item.text" :user_id="item.user_id" :color="item.completed ? 'green' : 'red'" />
                        <template v-else>{{ item.text }}</template>
                      </span>
                    </template>
                    <template v-else>{{ videoCallInfo(message.message).prefix }}</template>
                  </div>
                  <div style="display:flex; align-items:center; gap:6px;">
                    <v-icon size="16" color="primary">mdi-video</v-icon>
                    <span style="font-weight:600; font-size:0.85rem;">Video call</span>
                    <!-- Status badge -->
                    <span v-if="videoRoomEnded === null" class="vc-status-badge vc-status-badge--checking">
                      <v-icon size="10">mdi-dots-horizontal</v-icon> Checking
                    </span>
                    <span v-else-if="videoRoomEnded === false && activeParticipantCount > 0" class="vc-status-badge vc-status-badge--live">
                      <v-icon size="10" class="vc-status-pulse">mdi-circle</v-icon> In Progress
                    </span>
                    <!-- Participant names shown while in progress -->
                    <span v-if="videoRoomEnded === false && participants.length" class="vc-participants">
                      <v-icon size="11">mdi-account-multiple</v-icon>
                      {{ participants.map(p => p.name).join(', ') }}
                    </span>
                    <span v-else-if="videoRoomEnded === true" class="vc-status-badge vc-status-badge--ended">
                      <v-icon size="10">mdi-circle-outline</v-icon> Ended
                    </span>
                  </div>
                  <!-- Active room -->
                  <template v-if="videoRoomEnded === false">
                    <v-btn
                      size="small"
                      color="primary"
                      prepend-icon="mdi-video"
                      style="margin-top: 6px;"
                      @click.stop="joinVideoCall(videoCallInfo(message.message))"
                    >JOIN CALL</v-btn>
                    <!-- Completed recordings while room is still active -->
                    <div v-if="recordings.length" style="margin-top:8px;">
                      <div style="font-size:0.75rem; color:#666; margin-bottom:4px;">
                        <v-icon size="14">mdi-record-circle-outline</v-icon> Recordings
                      </div>
                      <div
                        v-for="(rec, i) in recordings"
                        :key="rec.video_recording_id"
                        style="margin-bottom:8px;"
                      >
                        <div style="display:flex; align-items:center; gap:6px;">
                          <v-icon size="14" color="grey">mdi-file-video-outline</v-icon>
                          <span style="font-size:0.78rem; color:#444;">Recording {{ i + 1 }}</span>
                          <span style="font-size:0.7rem; color:#999;">{{ formatDuration(rec.started_at, rec.ended_at) }}</span>
                          <v-btn
                            size="x-small"
                            variant="outlined"
                            :color="playingId === rec.video_recording_id ? 'grey' : 'primary'"
                            :prepend-icon="playingId === rec.video_recording_id ? 'mdi-stop' : 'mdi-play'"
                            :loading="loadingId === rec.video_recording_id"
                            @click.stop="togglePlay(videoCallInfo(message.message).roomId, rec.video_recording_id)"
                          >{{ playingId === rec.video_recording_id ? 'Close' : 'Play' }}</v-btn>
                        </div>
                        <video
                          v-if="playingId === rec.video_recording_id && playingUrl"
                          :src="playingUrl"
                          controls
                          preload="none"
                          controlslist="nodownload"
                          style="width:100%; max-width:400px; margin-top:6px; border-radius:6px;"
                        ></video>
                      </div>
                    </div>
                  </template>
                  <!-- Checking status -->
                  <v-btn
                    v-else-if="videoRoomEnded === null"
                    size="small"
                    color="primary"
                    prepend-icon="mdi-video"
                    style="margin-top: 6px;"
                    disabled
                  >JOIN CALL</v-btn>
                  <!-- Ended — show recordings -->
                  <template v-else>
                    <v-btn
                      size="small"
                      color="grey"
                      prepend-icon="mdi-video-off"
                      style="margin-top: 6px;"
                      disabled
                    >Meeting Ended</v-btn>
                    <!-- Recordings list -->
                    <div v-if="recordings.length" style="margin-top:8px;">
                      <div style="font-size:0.75rem; color:#666; margin-bottom:4px;">
                        <v-icon size="14">mdi-record-circle-outline</v-icon> Recordings
                      </div>
                      <div
                        v-for="(rec, i) in recordings"
                        :key="rec.video_recording_id"
                        style="margin-bottom:8px;"
                      >
                        <div style="display:flex; align-items:center; gap:6px;">
                          <v-icon size="14" color="grey">mdi-file-video-outline</v-icon>
                          <span style="font-size:0.78rem; color:#444;">Recording {{ i + 1 }}</span>
                          <span style="font-size:0.7rem; color:#999;">{{ formatDuration(rec.started_at, rec.ended_at) }}</span>
                          <v-btn
                            size="x-small"
                            variant="outlined"
                            :color="playingId === rec.video_recording_id ? 'grey' : 'primary'"
                            :prepend-icon="playingId === rec.video_recording_id ? 'mdi-stop' : 'mdi-play'"
                            :loading="loadingId === rec.video_recording_id"
                            @click.stop="togglePlay(videoCallInfo(message.message).roomId, rec.video_recording_id)"
                          >{{ playingId === rec.video_recording_id ? 'Close' : 'Play' }}</v-btn>
                        </div>
                        <video
                          v-if="playingId === rec.video_recording_id && playingUrl"
                          :src="playingUrl"
                          controls
                          preload="none"
                          controlslist="nodownload"
                          style="width:100%; max-width:400px; margin-top:6px; border-radius:6px;"
                        ></video>
                      </div>
                    </div>
                    <div v-else-if="recordingsLoaded" style="font-size:0.75rem; color:#999; margin-top:6px;">
                      No recordings available
                    </div>
                  </template>
                  <div v-if="videoCallInfo(message.message).suffix" style="margin-top:6px; white-space:pre-wrap; word-break:break-word;">{{ videoCallInfo(message.message).suffix }}</div>
                </template>
                <template v-else-if="message.convo_mentions && message.convo_mentions.length > 0">
                  <div v-for="item in splitbyMentions(message)" :key="item.index" style="display: inline">
                    <div v-if="item.user_id" style="display: inline">
                      <UserText :text="item.text" :user_id="item.user_id" :color="item.completed ? 'green' : 'red'" />
                    </div>
                    <template v-else>{{ item.text }}</template>
                  </div>
                </template>
                <template v-else>{{ message.message }}</template>
              </div>
              <div v-if="hasAttachments()">
                <div v-for="(value, index) in message.convo_attachments" :key="index">
                  <div v-if="value?.medium?.content_type.startsWith('audio')">
                    <AudioPlayer :src="`api/convos/internal/messages/${encodeURIComponent(value.convo_message_id)}/media/${encodeURIComponent(value.media_id)}`"></AudioPlayer>
                  </div>
                  <div v-if="value.medium && value.medium.content_type && (value.medium.content_type == 'video/webm'||value.medium.content_type == 'video/mp4' )">
                    <video controls preload="none"
                      :src="`api/convos/internal/messages/${encodeURIComponent(value.convo_message_id)}/media/${encodeURIComponent(value.media_id)}`"
                      width="400"></video>
                  </div>
                  <div v-else>
                    <div v-if="resolvedUrls[value.media_id]">
                      <template v-if="resolvedUrls[value.media_id].contentType == 'img'">
                        <div v-if="value.medium?.file_name" style="font-size:0.75rem; color:#555; margin-bottom:2px; word-break:break-all;">
                          {{ fileName(value) }}
                        </div>
                        <v-img v-on:click="openImg(resolvedUrls[value.media_id].url)" max-height="120" max-width="200"
                          :src="resolvedUrls[value.media_id].url"></v-img>
                      </template>
                      <template v-if="resolvedUrls[value.media_id].contentType == 'pdf'">
                        <a v-bind:href="'/api/convos/internal/messages/' + message.convo_message_id + '/media/' + value.media_id"
                          target="_blank">
                          <v-icon size="40" color="red">
                            mdi-file-pdf-box
                          </v-icon>
                          {{ pdfName(value)}}
                        </a>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
              <!-- display the message time -->
              <div class="time">
                {{ simplifyDate(message.date, "LT") }}
              </div>
              <div style="display: flex;justify-content: start;">
              <div
                 v-if="message.convo_tags && message.convo_tags.length"
                 style="display: flex; align-items: center; justify-content: flex-start; gap: 3px;"
               >
                <div v-for="tagObj in message.convo_tags" :key="tagObj.tag.tag_id">
                  <template v-if="tagsHashnew[tagObj.tag.tag_id]">
                 <v-tooltip
                   :text="tagsHashnew[tagObj.tag.tag_id].tag_name"
                   location="bottom"
                 >
                   <template #activator="{ props }">
                     <div v-bind="props">
                       <v-btn
                         icon
                         size="x-small"
                         color="white"
                         elevation="1"
                         style="margin: 0; border-radius: 50%; width: 22px; height: 22px; min-width: 22px; padding: 0;"
                       >
                         <v-icon
                           size="13"
                           :color="tagsHashnew[tagObj.tag.tag_id].tag_color"
                           @click.stop="removeTagFromMessage(message.convo_message_id, tagObj.tag.tag_id)"
                         >
                           mdi-{{ tagsHashnew[tagObj.tag.tag_id].tag_icon }}
                         </v-icon>
                       </v-btn>
                     </div>
                   </template>
                 </v-tooltip>
                </template>
               </div>
               </div>
                <div class="message-note" v-if="!editingNote" @click.stop="startEditing">
                  <v-icon size="12">mdi-note-text</v-icon>
                  <span>{{ displayNote }}</span>
                </div>

            <!-- Editable version -->
            <div v-else class="message-note-edit">
                  <div
                    class="editable-note"
                    contenteditable="true"
                    ref="editable"
                    @input="onInput"
                    @focus="moveCursorToEnd"
                  ></div>

                <div class="note-actions">
                  <v-icon size="16" color="green" @click.stop="saveNote">mdi-check</v-icon>
                  <v-icon size="16" color="red" @click.stop="cancelNote">mdi-close</v-icon>
                </div>
            </div>
           </div>
            </div>
          </template>
          <MessagePopup style="display: flex;height: fit-content;width: fit-content;"  :message="message" @showRefMsg="showRefMsg"  />
        </v-menu>
        <br>
        <MessagePopup v-if="showpopup  && isMobile && !isIOS" :message="message" @showRefMsg="showRefMsg" :class="message.who_sent === userId ? 'mobile-popup-self' : 'mobile-popup-friend'"/>
      </template>
      <template v-else>
        <div class="text-caption systemMessage">
          {{ message.message + " : [" + simplifyDate(message.date, "LT") + "]" }}
        </div>
      </template>
    </div>


  </div>
</template>

<script>
import UserPopup from "@/components/Avatar/UserPopup";
import UserText from "@/components/Avatar/UserText";
import MessagePopup from "@/components/Messenger/MessagePopup";
import ConvoSerice from "@/services/convoService";
import tagService from "../../services/tagService";
import VideoService from "@/services/videoService";
import { mapGetters } from "vuex";
import AudioPlayer from '../UIComponents/AudioPlayer.vue';
import EventBus from '../../eventBus';
import {ALERT} from '../../constants/alert'
export default {
  name: "Message",
  components: {
    UserPopup,
    UserText,
    MessagePopup,
    AudioPlayer
  },
  props: {
    message: { type: Object, default: null },
    prev: { type: Object, default: null },
    index: { type: Number, default: null },
    active: { type: Boolean, default: true },
    refrenceConvo: { type: Object, default: null },
  },
  data() {
    return {
      resolvedUrls: {},
      imgSrc: null,
      showpopup : false,
      showReference : false,
      editingNote: false,
      tempNote: "",
      oldNote: "",
      videoRoomEnded: null, // null = checking, true = closed, false = active/new
      activeParticipantCount: 0,
      participants: [],
      recordings: [],
      recordingsLoaded: false,
      loadingId: null,
      playingId: null,
      playingUrl: null,
    };
  },
  computed: {
    ...mapGetters(["userId"]),
    ...mapGetters("window",["isMobile","isIOS"]),
    ...mapGetters("tags", ["tagsHashnew"]),

displayNote() {
  // Safely extract note from both proxy and raw paths
  const note =
    this.message?.message_note ??
    this.message?._raw?.message_note ??
    null;

  return !note || note === "null" ? "Add note..." : note;
},
  },
  watch: {
    refrenceConvo: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchImg(newVal.convo_message_id, newVal.media_id);
        }
      }
    },
    'message.convo_message_id'(newId, oldId) {
      if (newId !== oldId) {
        this.resetVideoState();
        const callInfo = this.videoCallInfo(this.message?.message);
        if (callInfo) this.checkRoomStatus(callInfo.roomId);
      }
    },
    message(newMsg) {
      if (!newMsg?.convo_attachments?.length) return;
      newMsg.convo_attachments.forEach(attachment => {
        if (this.resolvedUrls[attachment.media_id]) return;
        const url = ConvoSerice.getConvoMediaUrl(newMsg.convo_message_id, attachment.media_id);
        const ct = attachment.medium?.content_type;
        this.resolvedUrls[attachment.media_id] = {
          url,
          contentType: ct?.includes('pdf') ? 'pdf' : 'img',
        };
      });
    },
  },
  mounted() {
    EventBus.$on("hidepopup",()=>{
      this.showpopup =false;
     });
    EventBus.$on('videoMeetingEnded', this.onMeetingEnded);
    EventBus.$on('videoParticipantUpdate', this.onParticipantUpdate);
    if (this.message.convo_attachments) {
      this.message.convo_attachments.forEach(attachment => {
        const url = ConvoSerice.getConvoMediaUrl(this.message.convo_message_id, attachment.media_id);
        const ct = attachment.medium?.content_type;
        this.resolvedUrls[attachment.media_id] = {
          url,
          contentType: ct?.includes('pdf') ? 'pdf' : 'img',
        };
      });
    }
    if(this.refrenceConvo){
      this.fetchImg(this.refrenceConvo.convo_message_id, this.refrenceConvo.media_id);
    }
    const callInfo = this.videoCallInfo(this.message?.message);
    if (callInfo) {
      this.checkRoomStatus(callInfo.roomId);
    }
  },
  beforeUnmount() {
    EventBus.$off('videoMeetingEnded', this.onMeetingEnded);
    EventBus.$off('videoParticipantUpdate', this.onParticipantUpdate);
  },
  methods: {
    resetVideoState() {
      this.videoRoomEnded = null;
      this.activeParticipantCount = 0;
      this.participants = [];
      this.recordings = [];
      this.recordingsLoaded = false;
      this.playingId = null;
      this.playingUrl = null;
    },
    onParticipantUpdate({ roomUuid, activeParticipants, participants }) {
      const callInfo = this.videoCallInfo(this.message?.message);
      if (callInfo && callInfo.roomId === roomUuid) {
        this.activeParticipantCount = activeParticipants;
        this.participants = participants || [];
      }
    },
    onMeetingEnded({ roomUuid }) {
      const callInfo = this.videoCallInfo(this.message?.message);
      if (callInfo && callInfo.roomId === roomUuid) {
        this.videoRoomEnded = true;
        this.loadRecordings(roomUuid);
      }
    },
    videoCallInfo(text) {
      if (!text) return null;
      // Captures: prefix | URL (non-whitespace) | suffix after URL
      const match = text.match(/^([\s\S]*?)Video call link - Join here: (\S+#\/video-call\/([\w-]+)(?:\?[^\s]*)?)([\s\S]*)$/);
      if (!match) return null;
      const url = match[2];
      const convoMatch = url.match(/[?&]convoId=(\d+)/);
      return {
        prefix: match[1].trim(),
        url,
        roomId: match[3],
        convoId: convoMatch ? convoMatch[1] : null,
        suffix: match[4].trim(),
      };
    },
    async checkRoomStatus(roomId) {
      try {
        const res = await VideoService.getRoomStatus(roomId);
        const { roomStatus, activeParticipants, participants } = res.data;
        this.activeParticipantCount = activeParticipants || 0;
        this.participants = participants || [];
        if (roomStatus === 'closed') {
          this.videoRoomEnded = true;
        } else {
          this.videoRoomEnded = false;
        }
        this.loadRecordings(roomId);
      } catch (err) {
        // 404 = room not created yet (link sent before first join) — allow joining
        // Any other error: default to allowing join rather than falsely showing "Ended"
        this.videoRoomEnded = false;
      }
    },
    async loadRecordings(roomUuid) {
      try {
        const res = await VideoService.getRecordingsByUuid(roomUuid);
        this.recordings = res.data || [];
      } catch {
        this.recordings = [];
      } finally {
        this.recordingsLoaded = true;
      }
    },
    async togglePlay(roomUuid, recordingId) {
      if (this.playingId === recordingId) {
        this.playingId = null;
        this.playingUrl = null;
        return;
      }
      this.loadingId = recordingId;
      try {
        const res = await VideoService.downloadRecordingByUuid(roomUuid, recordingId);
        this.playingUrl = res.data.url;
        this.playingId = recordingId;
      } catch (e) {
        console.error('Playback failed', e);
      } finally {
        this.loadingId = null;
      }
    },
    formatDuration(startedAt, endedAt) {
      if (!startedAt || !endedAt) return '';
      const secs = Math.round((endedAt - startedAt) / 1000);
      const m = Math.floor(secs / 60);
      const s = secs % 60;
      return `${m}m ${s}s`;
    },
    joinVideoCall(info) {
      const query = info.convoId ? `?convoId=${info.convoId}` : '';
      const url = `${window.location.origin}${window.location.pathname}#/video-call/${info.roomId}${query}`;
      window.open(url, '_blank');
    },
        parsedData(ltext) {
          // Split the string into key-value pairs based on the delimiter (", " and ": ")
          let keyValuePairs = ltext.split(", ");

          // Reduce the array of key-value pairs into an object
          let result = keyValuePairs.reduce((acc, pair) => {
            let [key, value] = pair.split(": ");
            acc[key] = value;
            return acc;
          }, {});

          return result;
    },
    toggleTime(){
      setTimeout(() => {
        if (this.showReference) {
          this.showpopup = true;
        } else {
          this.showpopup = !this.showpopup;
        }
        },0);
    },
    // showrefClicked(val){
    //   if(val){
    //     this.showReference = true;        
    //     this.showpopup = false;
    //   }else{
    //     this.showpopup = false;
    //     this.showReference = false;
    //   }
    // },
    openImg(url) {
      window.open(url);
    },
    childMethod(index){
console.log("ChildMethod",index);
    },
    async fetchImg(convo_message_id, media_id) {
      let res = await ConvoSerice.getConvoMediaUrl(convo_message_id, media_id)
      console.log("url",res)
      this.imgSrc = res;
    },
    handleReferenceClick() {
      console.log("called1")

      this.$emit('reference-click', this.refrenceConvo.convo_message_id);
    },
     showRefMsg(item) {
      console.log("showRefMsg",this.refrenceConvo)
     
      Object.keys(item).forEach(key => {
          this.refrenceConvo[key] = item[key];
        });
      this.refrenceConvo.ref= item.convo_message_id;
      this.$emit('reference-click', this.refrenceConvo.ref);
    },
    //Get the call Multimedia file URL
    async fetchAndStoreUrl(convo_message_id, media_id) {
      let res = await ConvoSerice.getConvoMediaUrl(convo_message_id, media_id)
      console.log(res)
      const response = await fetch(res, { method: 'HEAD' });
      const contentType = response.headers.get('content-type');
      console.log(contentType)
      this.$data.resolvedUrls[media_id] = {
        url: res,
        contentType: 'img'
      };
      if (contentType && contentType.includes('pdf')) {
        this.$data.resolvedUrls[media_id].contentType = 'pdf'
      }

    },
    async fetchImg(convo_message_id, media_id) {
      let res = await ConvoSerice.getConvoMediaUrl(convo_message_id, media_id)
      return res;
    },
    openImg(url) {
      window.open(url);
    },
    // async getFileUrl(convo_message_id, media_id) {
    //   let res = await ConvoSerice.getConvoMediaUrl(convo_message_id, media_id)
    //   return res;
    // },
    showDate() {
       if (!this.active) return false;
       if (!this.message || !this.message.date) return false;
       // always show date on first message
       if (this.index === 0 || !this.prev || !this.prev.date) return true;
       // safe compare
       return (this.simplifyDate(this.message.date, 'LL') !== this.simplifyDate(this.prev.date, 'LL'));
    },
    hasAttachments() {
      return this.message.convo_attachments && this.message.convo_attachments.length > 0;
    },
    // the possible formats can be found at dayjs.com
    simplifyDate(date, format) {
      return this.dayjs(date).format(format);
    },
    // returns true if the two dates are within n minutes
    withinMinutes(date1, date2, n) {
      return this.dayjs(date1).diff(this.dayjs(date2), "minute") <= n;
    },
    pdfName(data1){
      let name = (data1?.medium?.file_name.split("/")[3].substring(15));
      name =name? name:" ";
      return name
      },
    fileName(attachment) {
      const raw = attachment?.medium?.file_name;
      if (!raw) return '';
      const parts = raw.split('/');
      const last = parts[parts.length - 1];
      const stripped = last.substring(15);
      return stripped || last;
    },
    // splits the message by mentions
    splitbyMentions(message) {
      let splitIndices = [];
      if (message.convo_mentions) {
        message.convo_mentions.forEach((m) => {
          splitIndices.push(m.index_start);
          splitIndices.push(m.index_end);
        });
      }
      let acc = -1;
      return [0, ...splitIndices].map((n, i, m) => {
        return {
          text: message.message.slice(n, m[i + 1]),
          user_id: i % 2 !== 0 && message.convo_mentions ? message.convo_mentions[(acc += 1)]?.user_id : null,
          index: i,
          completed: i % 2 !== 0 && message.convo_mentions ? message.convo_mentions[acc]?.completed : null,
        };

      });
    },
    splitPrefixByMentions(message, prefix) {
      if (!message.convo_mentions || !message.convo_mentions.length || !prefix) {
        return [{ text: prefix, user_id: null, index: 0, completed: null }];
      }
      const prefixLen = prefix.length;
      const prefixMentions = message.convo_mentions.filter(
        m => m.index_start !== undefined && m.index_start < prefixLen
      );
      if (!prefixMentions.length) {
        return [{ text: prefix, user_id: null, index: 0, completed: null }];
      }
      return this.splitbyMentions({ message: prefix, convo_mentions: prefixMentions });
    },
   async removeTagFromMessage(messageId, tagId) {
    try{
      let data={tagId,messageId,convo_id:this.message.convo_id};
      let res = await tagService.removeConvoTag(data);
         if(res.status == 200){
            this.message.convo_tags = this.message.convo_tags.filter(
        (t) => t.tag.tag_id !== tagId
      );
          EventBus.$emit("alert", {
           message: "Tag Removed Successfully",
           type: ALERT.SUCCESS,
         });
        }
      } catch (error) {
        EventBus.$emit("alert", {
           message: "Failed To Remove Tag",
           type: ALERT.ERROR,
         });
        console.error( "Failed To Remove Tag",error);
      }
    },
    startEditing() {
  const current =this.message?.message_note ?? this.message?._raw?.message_note ?? "";
  this.oldNote = current;
  this.tempNote = current === "null" ? "" : current || "";
  this.editingNote = true;
      this.$nextTick(() => {
        const el = this.$refs.editable;
        if (el) {
          el.innerText = this.tempNote;
          this.moveCursorToEnd();
        }
      });
    },
    onInput(e) {
      this.tempNote = e.target.innerText;
    },
    moveCursorToEnd() {
      const el = this.$refs.editable;
      if (!el) return;
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(el);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    },
async saveNote() {
  try {
    const noteText = this.tempNote.trim();

    if (!noteText) {
      EventBus.$emit("alert", {
        message: "Note cannot be empty!",
        type: ALERT.ERROR,
      });
      return;
    }
      
    // Save to backend
    await ConvoSerice.addConvoNotes(this.message.convo_message_id, {
      message_note: noteText,
      convo_id:this.message.convo_id
    });

    // Update local state
    if (this.message) {
      this.message.message_note = noteText;

      if (this.message._raw) {
        this.message._raw.message_note = noteText;
      }
    }
  } catch (err) {
    console.error("error", err);

    // Restore the previous note value
    if (this.message) {
      this.message.message_note = this.oldNote;

      if (this.message._raw) {
        this.message._raw.message_note = this.oldNote;
      }
    }
  }

  this.editingNote = false;
},
cancelNote() {
  if (!this.message) return;
  this.message.message_note = this.oldNote;
  if (this.message._raw) {
    this.message._raw.message_note = this.oldNote;
  }
  this.editingNote = false;
},
  }
};
</script>

<style scoped>
.chat-bubble-container {
  white-space: pre-wrap;
  padding: 5px 0;
  display: flex;
}

.chat-bubble {
  text-align: left;
  font-size: 0.9rem;
  padding: 0.4rem 0.5rem;
  border-radius: 0.5rem;
  width: auto;
  max-width: 80%;
}

/* Friend message */
.msg-friend {
  background-color: #e3e3e3;
  margin-left: 0.3rem;
  margin-right: auto;
}

/* Self message */
.msg-self {
  background-color: #d9f7dc;
  margin-left: auto;
  margin-right: 0.3rem;
  align-self: flex-end;
}

.systemMessage {
  flex: 1;
  color: #6e6e6e;
}

.separator {
  display: flex;
  align-items: center;
}

.separator::before,
.separator::after {
  content: "";
  flex: 1;
  border-bottom: 1px solid #e0e0e0;
}

.date {
  border: 1px solid #e0e0e0;
  border-radius: 0.5rem;
  padding: 0.1rem 0.5rem;
}

.time {
  font-size: 0.6rem;
  text-align: right;
  color: #90a09f;
}

.mobile-popup-self {
  position: absolute;
  left: 70%;
}

.mobile-popup-friend {
  position: absolute;
  right: 55%;
}

.message-note-box {
  margin-top: 4px;
  margin-left: 0.6rem;
}

.message-note {
  font-size: 0.7rem;
  color: #6c757d;
  background: #f6f6f6;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #e2e2e2;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.message-note:hover {
  background: #eaeaea;
}

.message-note-edit {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid #dcdcdc;
  border-radius: 4px;
  padding: 2px 6px;
}

.editable-note {
  flex: 1;
  min-width: 80px;
  font-size: 0.7rem;
  color: #333;
  outline: none;
  border: none;
  background: transparent;
}

.note-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.note-actions .v-icon {
  cursor: pointer;
  transition: transform 0.2s ease;
}

.note-actions .v-icon:hover {
  transform: scale(1.1);
}

/* Video call status badges */
.vc-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.65rem;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.vc-status-badge--checking {
  background: #f0f0f0;
  color: #999;
}

.vc-status-badge--live {
  background: #e8f5e9;
  color: #2e7d32;
}

.vc-status-badge--ended {
  background: #f5f5f5;
  color: #757575;
}

.vc-status-pulse {
  animation: vc-blink 1.4s ease-in-out infinite;
}

.vc-participants {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.68rem;
  color: #555;
  margin-left: 2px;
}

@keyframes vc-blink {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.25; }
}
</style>
